/* ============================================================================
 * NEWS_DESK › Blog — the publishing surface
 * ----------------------------------------------------------------------------
 * Mock-to-live: ONE component, two sources. It reads Lens for real; when the API
 * cannot be reached it falls back to fixtures and says `specimen` on the screen
 * — never a fixture passed off as live, and never an empty desk pretending the
 * rail is down quietly.
 *
 * The split visualiser renders the money path, because "automated revenue
 * splits" is the part a reader cannot otherwise see. It is drawn from constants
 * this repo already carries (0xSplits SplitMain, verified as a contract on
 * mainnet, Base and Base Sepolia) plus Lens' own collect-fee limit, which is
 * narrower than the obvious reading: at most FIVE flat BPS recipients per post.
 * ========================================================================= */

import { useEffect, useMemo, useState } from 'react';
import {
  LENS_API,
  LENS_CAPABILITIES,
  SAMPLE_POSTS,
  fetchLatestPosts,
  type LensPost,
} from '../lib/lens';
import { SPLITS_MAIN } from '../lib/immersion';
import { INSTRUMENT_ANCHOR } from '../lib/matrix';

/** Where a subscription is meant to land, in basis points of the whole. */
const SPLIT_TERMS = [
  { label: 'Writing pool', bps: 7000, note: 'the authors, split among themselves by the pool contract' },
  { label: 'Desk and hosting', bps: 2000, note: 'the desk that edits, hosts and pays for the rail' },
  { label: 'Treasury', bps: 1000, note: 'reserve, held against the next cohort' },
] as const;

const STAT_KEYS = [
  'comments',
  'reposts',
  'quotes',
  'collects',
  'tips',
  'reactions',
  'bookmarks',
] as const;

function pct(bps: number): number {
  return bps / 100;
}

function short(v: string, n = 10): string {
  return v.length > n ? `${v.slice(0, n)}…` : v;
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toISOString().slice(0, 10);
}

export function NewsDeskBlog() {
  const [live, setLive] = useState<LensPost[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [reading, setReading] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await fetchLatestPosts('TEN');
      if (cancelled) return;
      if (res.status === 'ok') {
        setLive(res.data);
        setProblem(null);
      } else {
        setProblem(res.reason);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isSpecimen = live === null;
  const posts = live ?? SAMPLE_POSTS;
  const post = useMemo(
    () => posts.find((p) => p.id === reading) ?? posts[0],
    [posts, reading],
  );

  /** Article-shaped posts only — the firehose is mostly not articles. */
  const articles = posts.filter((p) => p.kind !== 'unresolved');

  const totals = useMemo(
    () =>
      STAT_KEYS.reduce<Record<string, number>>((acc, k) => {
        acc[k] = posts.reduce((n, p) => n + p.stats[k], 0);
        return acc;
      }, {}),
    [posts],
  );

  return (
    <div className="dsp stack">
      {/* ── band: the rail ───────────────────────────────────────────────── */}
      <div className="page__bar">
        <div className="grow">
          <p className="section-h">
            publishing rail <span className="dim">lens</span>
          </p>
          <p className="cap">
            Posts are read from the Lens API. Bodies are content-addressed; this
            desk renders them, it does not store them.
          </p>
        </div>
        <div className="row" style={{ gap: 6 }}>
          {isSpecimen ? (
            <span className="badge badge-beta">specimen</span>
          ) : (
            <span className="badge badge-live">live</span>
          )}
          <span className="badge badge-claude">
            {LENS_API.replace(/^https:\/\//, '')}
          </span>
        </div>
      </div>

      {isSpecimen && (
        <div className="notice notice--brass">
          <span className="tick" aria-hidden="true" />
          <span>
            <strong>Specimen.</strong> {problem ?? 'reading…'} — the rows below are
            fixtures, not posts from the rail.
          </span>
        </div>
      )}

      {!isSpecimen && articles.length === 0 && (
        <div className="notice">
          <span className="tick" aria-hidden="true" />
          <span>
            The rail answered, and <strong>none of the ten newest posts are
            articles</strong>. A publication feed has to be narrowed by author or
            content focus — the global firehose is not one.
          </span>
        </div>
      )}

      {/* ── band: the roll ───────────────────────────────────────────────── */}
      <div>
        <div className="page__bar">
          <div className="grow">
            <p className="section-h">the roll</p>
            <p className="cap">Select a row to read it in the desk.</p>
          </div>
          <span className="chip">
            {posts.length} row{posts.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="sheet sheet-rows" style={{ marginTop: 'var(--s-3)' }}>
          {posts.map((p) => (
            <div
              key={p.id}
              className="lrow"
              role="button"
              tabIndex={0}
              aria-current={post?.id === p.id ? 'true' : undefined}
              onClick={() => setReading(p.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setReading(p.id);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <span className="mono" style={{ flex: '0 0 78px' }}>
                {when(p.timestamp)}
              </span>
              <span className="lrow__t">
                {p.title ?? (
                  <span className="dim">no title — {p.kind}</span>
                )}
                <span className="sub">
                  {p.author.handleFull ?? short(p.author.address, 16)}
                  {p.tags.length > 0 && ` · ${p.tags.join(' · ')}`}
                </span>
              </span>
              <span className="mono">{p.stats.reactions} ✳</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── band: the reader ─────────────────────────────────────────────── */}
      {post && (
        <div className="sheet">
          <div className="row row--between" style={{ alignItems: 'flex-start' }}>
            <div className="grow">
              <p className="section-h">{post.kind}</p>
              <h2 className="h2">{post.title ?? 'Untitled post'}</h2>
            </div>
            <span className="badge badge-claude">{when(post.timestamp)}</span>
          </div>

          <p className="cap" style={{ marginTop: 'var(--s-3)' }}>
            {post.body ?? 'No body in this metadata member — the post type is not one this desk renders yet.'}
          </p>

          <dl className="kv" style={{ marginTop: 'var(--s-3)' }}>
            <dt>author</dt>
            <dd className="mono">{post.author.handleFull ?? post.author.address}</dd>
            <dt>address</dt>
            <dd className="mono">{post.author.address}</dd>
            <dt>content uri</dt>
            <dd className="mono">{post.contentUri}</dd>
            <dt>slug</dt>
            <dd className="mono">{post.slug}</dd>
          </dl>

          {/* The publication NODE — what makes a post collectable and payable,
              as opposed to merely readable. */}
          <div className="page__bar" style={{ marginTop: 'var(--s-4)' }}>
            <p className="section-h">
              publication node <span className="dim">collector + split hooks</span>
            </p>
            <span className="badge badge-claude">not wired</span>
          </div>
          <dl className="kv">
            <dt>anchored to</dt>
            <dd className="mono">{INSTRUMENT_ANCHOR} · does not resolve yet</dd>
            <dt>collect hook</dt>
            <dd className="cap">
              Lens collect action module — simple, or multi-recipient (at most five
              recipients, flat basis points)
            </dd>
            <dt>split hook</dt>
            <dd className="mono">{SPLITS_MAIN}</dd>
            <dt>collect fee</dt>
            <dd>
              <span className="badge badge-claude">no price set</span>
            </dd>
            <dt>revenue routed</dt>
            <dd className="mono">0 — no split created</dd>
          </dl>
          <p className="cap" style={{ marginTop: 'var(--s-3)' }}>
            Reading a post works today; being paid for one does not. The node needs
            a franchise address before it can own a collect fee, and a split before
            it can route one.
          </p>
        </div>
      )}

      {/* ── band: engagement ─────────────────────────────────────────────── */}
      <div>
        <div className="page__bar">
          <div className="grow">
            <p className="section-h">
              engagement <span className="dim">across {posts.length} rows</span>
            </p>
            <p className="cap">
              Counters Lens actually returns. There is no search-console data in
              the protocol, so nothing here claims a ranking.
            </p>
          </div>
        </div>
        <div className="grid4" style={{ marginTop: 'var(--s-3)' }}>
          {STAT_KEYS.slice(0, 4).map((k) => (
            <div className="stat-tile" key={k}>
              <p className="label">{k}</p>
              <p className="val">{totals[k]}</p>
              <p className="sub">{isSpecimen ? 'specimen totals' : 'live totals'}</p>
            </div>
          ))}
        </div>
        <div className="grid3" style={{ marginTop: 'var(--s-3)' }}>
          {STAT_KEYS.slice(4).map((k) => (
            <div className="stat-tile" key={k}>
              <p className="label">{k}</p>
              <p className="val">{totals[k]}</p>
              <p className="sub">{isSpecimen ? 'specimen totals' : 'live totals'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── band: where the money goes ───────────────────────────────────── */}
      <div className="sheet">
        <div className="page__bar">
          <div className="grow">
            <p className="section-h">
              revenue split <span className="dim">on collect</span>
            </p>
            <p className="cap">
              Terms: where a subscription lands the moment it lands. It has to
              total 100.
            </p>
          </div>
          <span className="chip">
            {SPLIT_TERMS.reduce((n, t) => n + pct(t.bps), 0)}%
          </span>
        </div>

        <div className="stack" style={{ marginTop: 'var(--s-3)' }}>
          {SPLIT_TERMS.map((t) => (
            <div key={t.label}>
              <div className="row row--between">
                <span className="micro micro--ink">{t.label}</span>
                <span className="mono">{pct(t.bps)}%</span>
              </div>
              <div
                className="prog-track"
                role="progressbar"
                aria-label={`${t.label} share`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct(t.bps)}
                style={{ marginTop: 6 }}
              >
                <i className="prog-fill" style={{ width: `${pct(t.bps)}%` }} />
              </div>
              <p className="cap" style={{ marginTop: 4 }}>
                {t.note}
              </p>
            </div>
          ))}
        </div>

        <dl className="kv" style={{ marginTop: 'var(--s-4)' }}>
          <dt>distribution contract</dt>
          <dd className="mono">{SPLITS_MAIN}</dd>
          <dt>lens collect recipients</dt>
          <dd>
            <span className="badge badge-beta">max five, flat bps</span>
          </dd>
          <dt>execution</dt>
          <dd>
            <span className="badge badge-admin">not wired</span>
          </dd>
        </dl>

        <p className="cap" style={{ marginTop: 'var(--s-3)' }}>
          The two mechanisms are not interchangeable. Lens' multi-recipient collect
          module splits a collect fee across <strong>at most five recipients</strong>,
          flat, in basis points. A nested or arbitrary split graph is 0xSplits'
          job. The pattern that uses both: make the 0xSplits address <em>one</em>
          of the collect recipients, and let the contract do the rest.
        </p>
      </div>

      {/* ── band: what the rail does and does not give us ────────────────── */}
      <div>
        <p className="section-h">rail capabilities</p>
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Lens</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {LENS_CAPABILITIES.map((c) => (
                <tr key={c.cap}>
                  <td>{c.cap}</td>
                  <td>
                    {c.lens ? (
                      <span className="badge badge-live">yes</span>
                    ) : (
                      <span className="badge badge-beta">no</span>
                    )}
                  </td>
                  <td className="cap">{c.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
